import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 3
import { PanelFrame } from '../workspace/PanelFrame';
import { Users, Plus, BookHeart, ChevronDown, Sparkles, Loader2, Trash2, Search, Network, Maximize2, Minimize2, GitMerge, ArrowRightLeft } from 'lucide-react';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';
import { ARCHETYPES, ARCHETYPE_GROUPS } from '@/lib/archetypes';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { toast } from 'sonner';
import axios from 'axios';
import { seriesSyncApi, charactersApi } from '@/lib/api';
import StoryBibleDialog from '@/components/dialogs/StoryBibleDialog';
import CharacterDedupeDialog from '@/components/dialogs/CharacterDedupeDialog';
import CoachHint from '@/components/coach/CoachHint';
import { characterRowHint } from '@/components/coach/coachHints';
import FiveTierBible from './character-bible/FiveTierBible';
import StoryBibleSummary from './character-bible/StoryBibleSummary';
import RelationshipMapPanel from './character-bible/RelationshipMapPanel';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export function CharacterBiblePanel({ onClose, maximized, onMaximize }) {
  const { t } = useTranslation();
  const { characters, setCharacters, activeCharacterId, setActiveCharacterId, updateCharacter,
            addCharacter, removeCharacter, project, setProject, updateProject } = usePW();
  const ch = characters.find((c) => c.id === activeCharacterId) || characters[0];
  const bible = project?.story_bible;
  const hasBible = !!(bible && (bible.story_arc || bible.mood || bible.worldbuilding ||
                                (Array.isArray(bible.themes) && bible.themes.length)));
  const [bibleOpen, setBibleOpen] = React.useState(false);
  const [bibleDialogOpen, setBibleDialogOpen] = React.useState(false);
  const [charPickerOpen, setCharPickerOpen] = React.useState(false);
  // ── Full-screen modes ────────────────────────────────────────────────────
  const [fsDialog, setFsDialog] = React.useState(false);   // modal overlay full-screen
  React.useEffect(() => { if (hasBible && bible?.claude_parsed) setBibleOpen(true); }, [hasBible, bible?.claude_parsed]);

  // ── Duplicate character detection state ─────────────────────────────────
  const [dupCheckResult, setDupCheckResult] = React.useState(null);  // {name, matches}
  const [dupDialogOpen, setDupDialogOpen] = React.useState(false);
  const [dedupeOpen, setDedupeOpen] = React.useState(false);

  // ── Bulk Series Sync ──────────────────────────────────────────────────────
  const [bulkSyncOpen,      setBulkSyncOpen]      = React.useState(false);
  const [bulkSyncProjects,  setBulkSyncProjects]  = React.useState([]);
  const [bulkSyncTargetId,  setBulkSyncTargetId]  = React.useState('');
  const [bulkSyncing,       setBulkSyncing]        = React.useState(false);

  const openBulkSync = async () => {
    setBulkSyncOpen(true);
    if (bulkSyncProjects.length === 0) {
      try {
        const r = await axios.get(`${API}/projects`, { withCredentials: true });
        const list = Array.isArray(r.data) ? r.data : (r.data.projects || []);
        setBulkSyncProjects(list.filter(p => p.id !== project?.id));
      } catch { /* ignore */ }
    }
  };

  const runBulkSync = async () => {
    if (!bulkSyncTargetId) { toast.error('Select a target project.'); return; }
    setBulkSyncing(true);
    try {
      const res = await seriesSyncApi.bulkPush(project.id, bulkSyncTargetId);
      toast.success(`Synced ${res.synced} character${res.synced !== 1 ? 's' : ''} (${res.created} created, ${res.updated} updated).`);
      setBulkSyncOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk sync failed.');
    } finally { setBulkSyncing(false); }
  };

  // Count sync-enabled chars for the button label
  const syncEnabledCount = characters.filter(c => c.series_carryover).length;

  const checkForDuplicateCharacter = React.useCallback(async (newName, currentId) => {
    if (!project?.id || !newName.trim()) return;
    // Skip generic placeholder names
    if (/^new character$/i.test(newName.trim())) return;
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/characters/check-name-alias`,
        { name: newName.trim(), current_character_id: currentId || '' },
        { withCredentials: true, timeout: 20000 },
      );
      if (r.data?.is_duplicate && r.data?.potential_matches?.length) {
        setDupCheckResult({ name: newName, matches: r.data.potential_matches });
        setDupDialogOpen(true);
      }
    } catch { /* silent — don't block the user */ }
  }, [project?.id]);
  // iter55z-q — First-time contextual tours for the two bibles.
  React.useEffect(() => {
    // Small delay so the panel is fully painted before the spotlight measures.
    const t1 = setTimeout(() => {
      import('@/lib/tours').then(({ runTour }) => runTour('character-bible-open'));
    }, 900);
    const t2 = hasBible ? setTimeout(() => {
      import('@/lib/tours').then(({ runTour }) => runTour('story-bible-open'));
    }, 3200) : null;
    return () => { clearTimeout(t1); if (t2) clearTimeout(t2); };
  }, [hasBible]);

  // iter55h — Archetype picker state.
  const [pending, setPending] = React.useState(null);
  const [applying, setApplying] = React.useState(false);

  // iter55t — Delete-confirmation state.
  const [confirmingDelete, setConfirmingDelete] = React.useState(null);

  // iter55t — Add-character handler wired to the panel's "+ Add" button.
  // Optimistically inserts a "New Character" via the store and focuses
  // the roster on it so the user can rename it inline.
  const handleAdd = React.useCallback(async () => {
    await addCharacter();
  }, [addCharacter]);

  // Feb-2026 — "Map Relationships": AI reads the Bible + manuscript and
  // proposes an editable relationship row per connected character pair.
  const [relMapLoading, setRelMapLoading] = React.useState(false);
  const [relMapSaving, setRelMapSaving] = React.useState(false);
  const relationshipRows = bible?.relationship_map;
  // FastAPI 422s return `detail` as an array of validation-error objects,
  // not a string — passing that straight to toast.error would try to
  // render an object as a React child. Flatten to plain text defensively.
  const relMapErr = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join('; ') || fallback;
    return fallback;
  };
  const handleGenerateRelationshipMap = React.useCallback(async () => {
    if (!project?.id) return;
    if ((bible?.relationship_map || []).length > 0) {
      const ok = window.confirm('This replaces the current relationship map with a fresh AI pass. Any manual edits will be lost. Continue?');
      if (!ok) return;
    }
    setRelMapLoading(true);
    try {
      const r = await axios.post(`${API}/projects/${project.id}/relationships/map`, {}, { withCredentials: true });
      const rows = r.data.relationship_map || [];
      setProject((p) => ({ ...p, story_bible: { ...(p.story_bible || {}), relationship_map: rows } }));
      toast.success(rows.length
        ? `Mapped ${rows.length} relationship${rows.length === 1 ? '' : 's'} from your Bible + manuscript.`
        : 'No clear relationships found yet — try adding more to the Bible or manuscript.');
    } catch (e) {
      toast.error(relMapErr(e, 'Could not map relationships right now.'));
    } finally {
      setRelMapLoading(false);
    }
  }, [project?.id, setProject, bible?.relationship_map]);
  const handleSaveRelationshipMap = React.useCallback(async (rows) => {
    if (!project?.id) return;
    setRelMapSaving(true);
    try {
      const r = await axios.put(`${API}/projects/${project.id}/relationships/map`, { rows }, { withCredentials: true });
      setProject((p) => ({ ...p, story_bible: { ...(p.story_bible || {}), relationship_map: r.data.relationship_map } }));
      toast.success('Relationship map saved.');
    } catch (e) {
      toast.error(relMapErr(e, 'Could not save your edits.'));
    } finally {
      setRelMapSaving(false);
    }
  }, [project?.id, setProject]);

  // 2026-06 — "Read manuscript": the project's model (Opus on the
  // Thorn Accord novels) reads every drafted chapter and fills the
  // Story Bible + one card per character it finds. Runs as a
  // background job (a full novel takes 1-3 minutes).
  const [readingMs, setReadingMs] = React.useState(false);
  const handleReadManuscript = React.useCallback(async () => {
    if (!project?.id || readingMs) return;
    setReadingMs(true);
    toast.info('Reading the manuscript…', { description: 'This takes a minute or two on a full novel.' });
    try {
      await axios.post(`${API}/projects/${project.id}/story-bible/autofill`, {}, { withCredentials: true });
      const deadline = Date.now() + 10 * 60 * 1000;
      for (;;) {
        await new Promise((r) => setTimeout(r, 6000));
        const s = await axios.get(`${API}/projects/${project.id}/story-bible/autofill/status`, { withCredentials: true });
        const job = s.data.job;
        if (!job || job.status === 'running') {
          if (Date.now() > deadline) throw new Error('Timed out waiting for the read to finish.');
          continue;
        }
        if (job.status === 'error') throw new Error(job.error || 'The read failed.');
        const [p, c] = await Promise.all([
          axios.get(`${API}/projects/${project.id}`, { withCredentials: true }),
          axios.get(`${API}/projects/${project.id}/characters`, { withCredentials: true }),
        ]);
        setProject(p.data);
        setCharacters(c.data || []);
        toast.success('Story Bible filled from the manuscript.', {
          description: `${job.chapters_read} chapters read · ${job.characters_created} new character cards · ${job.characters_updated} updated.`,
        });
        break;
      }
    } catch (e) {
      toast.error(relMapErr(e, e?.message || 'Could not read the manuscript right now.'));
    } finally {
      setReadingMs(false);
    }
  }, [project?.id, readingMs, setProject, setCharacters]);

  // iter-arc1 — "Fill from synopsis": runs the Novel Bible Inference Engine
  // on the project's synopsis to populate ALL bibles at once.
  const [inferringBibles, setInferringBibles] = React.useState(false);
  const hasSynopsis = !!project?.synopsis?.trim();
  const handleFillFromSynopsis = React.useCallback(async () => {
    if (!project?.id || inferringBibles) return;
    setInferringBibles(true);
    toast.info('Populating all bibles from synopsis…', {
      description: 'Story Bible · World Builder · Plot · Characters · Relationships',
    });
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/bible/infer-from-synopsis`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      const [p, c] = await Promise.all([
        axios.get(`${API}/projects/${project.id}`, { withCredentials: true }),
        axios.get(`${API}/projects/${project.id}/characters`, { withCredentials: true }),
      ]);
      setProject(p.data);
      setCharacters(c.data || []);
      const chars = r.data.characters_created + (r.data.characters_updated || 0);
      const rels  = r.data.relationships_mapped || 0;
      toast.success('All bibles populated from synopsis.', {
        description: `${chars} character${chars === 1 ? '' : 's'} · ${rels} relationship${rels === 1 ? '' : 's'} mapped`,
      });
      // Auto-open Plot Blueprint panel so the writer lands on fresh beats immediately
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'plotex' }));
      }, 300);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not infer bibles from synopsis.');
    } finally {
      setInferringBibles(false);
    }
  }, [project?.id, inferringBibles, setProject, setCharacters]);

  // Auto-populate: when the panel first opens and the Bible is completely empty,
  // auto-trigger a read-manuscript pass (once per project per browser session).
  React.useEffect(() => {
    if (!project?.id) return;
    const sessionKey = `loom:bible-autofill:${project.id}`;
    if (sessionStorage.getItem(sessionKey)) return;  // already ran this session
    const bibleEmpty = !hasBible;
    const charsEmpty = characters.length === 0;
    if (!bibleEmpty || !charsEmpty) return;  // bible already populated
    sessionStorage.setItem(sessionKey, '1');
    // Small delay so the panel finishes mounting before the toast fires
    const t = setTimeout(() => {
      handleReadManuscript();
    }, 1200);
    return () => clearTimeout(t);
  }, [project?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Tools → "Read Manuscript → Fill Story Bible" fires this.
  React.useEffect(() => {
    const run = () => handleReadManuscript();
    window.addEventListener('story-bible-autofill', run);
    return () => window.removeEventListener('story-bible-autofill', run);
  }, [handleReadManuscript]);

  // Coach archetype suggestion → "Apply in Character Bible" fires this.
  // Finds the character by name, selects them, and pre-fills the quick-search.
  const charSearchRef = React.useRef(null);
  const [charSearch, setCharSearch] = React.useState('');

  React.useEffect(() => {
    const handler = (e) => {
      const targetName = (e.detail?.characterName || '').trim();
      const targetLow  = targetName.toLowerCase();
      if (!targetLow || !characters.length) return;
      const match = characters.find((c) => (c.name || '').toLowerCase() === targetLow);
      if (match) {
        setActiveCharacterId(match.id);
        // Pre-fill and focus the quick-search bar so the user sees context
        setCharSearch(match.name || '');
        setTimeout(() => {
          charSearchRef.current?.focus();
          charSearchRef.current?.select();
          const el = document.querySelector(`[data-testid="character-bible-option-${match.id}"]`);
          el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 200);
      } else {
        // Character not yet in bible — still put name in search so user can add
        setCharSearch(targetName);
        setTimeout(() => charSearchRef.current?.focus(), 200);
      }
    };
    window.addEventListener('loom:open-character-bible', handler);
    return () => window.removeEventListener('loom:open-character-bible', handler);
  }, [characters, setActiveCharacterId]);


  // iter55t — Persist Identity/Description/Voice edits.
  const persistField = React.useCallback((key, raw) => {
    if (!ch) return;
    let value = raw;
    if (key === 'age') {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n) || n < 0) return;
      value = n;
    }
    if ((ch[key] ?? '') === value) return;   // no-op — nothing changed
    updateCharacter(ch.id, { [key]: value });
    // Duplicate check only for the name field
    if (key === 'name') checkForDuplicateCharacter(value, ch.id);
    // For description/voice changes, save a draft entry so the change_cue
    // gets accumulated and picked up on the next Commit & Rebuild.
    if ((key === 'description' || key === 'voice') && project?.id && value.trim()) {
      const fieldLabel = key === 'description'
        ? `Character "${ch.name || 'unnamed'}" — Description`
        : `Character "${ch.name || 'unnamed'}" — Voice`;
      axios.put(`${API}/projects/${project.id}/bible-drafts`, {
        field_key: `char_${ch.id}_${key}`,
        draft_value: String(value),
        field_label: fieldLabel,
      }, { withCredentials: true }).catch(() => {});
    }
  }, [ch, project?.id, updateCharacter, checkForDuplicateCharacter]);

  const applyArchetype = async () => {
    if (!pending || !ch) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/characters/${ch.id}/apply-archetype`, {
        archetype_id:    pending.id,
        archetype_name:  pending.name,
        archetype_blurb: pending.blurb,
      }, { withCredentials: true });
      // Push the returned character into the store.
      updateCharacter(ch.id, {
        archetype:   r.data.archetype,
        traits:      r.data.traits,
        description: r.data.description,
      });
      toast.success(`Archetype committed: ${pending.name}`,
        { description: 'Claude re-tuned the sliders and description.' });
      setPending(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply archetype — try again.');
    } finally { setApplying(false); }
  };

  const updateTrait = (k, v) => {
    if (!ch) return;
    const traits = (ch.traits || []).map((t) => (t.k === k ? { ...t, v } : t));
    updateCharacter(ch.id, { traits });
  };

  // iter55v — Empty roster state. A brand-new novel starts blank; the
  // Bible populates automatically once the user imports a synopsis /
  // three-act summary / manuscript and Claude extracts characters, or
  // manually via the "+ Add" button. This message replaces the stale
  // "Loading characters…" copy which was misleading when the empty
  // state was intentional.
  if (!ch) {
    return (
      <PanelFrame
        title={t('characterBible.title')}
        icon={Users}
        panelId="characters"
        onClose={onClose}
        onMaximize={onMaximize}
        maximized={maximized}
        aiHint="bible"
        actions={
          <div className="flex items-center gap-1">
            {hasSynopsis && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-1.5 text-[13px] text-primary hover:bg-primary/10"
                onClick={handleFillFromSynopsis}
                disabled={inferringBibles}
                data-testid="bible-fill-from-synopsis-btn-empty"
                title="Populate all bibles from the project synopsis"
              >
                {inferringBibles ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Fill Bibles
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-[13px]"
              onClick={handleReadManuscript}
              disabled={readingMs}
              data-testid="bible-read-manuscript-btn-empty"
              title="Read the manuscript and fill the Story Bible"
            >
              {readingMs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Read
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-[13px]"
              onClick={handleAdd}
              data-testid="character-add-btn-empty"
              title={t('characterBible.addCharacter')}
            >
              <Plus className="h-3.5 w-3.5" /> {t('common.add')}
            </Button>
          </div>
        }
      >
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center" data-testid="bible-empty-state">
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Users className="h-5 w-5" />
          </div>
          <div className="text-[13px] font-semibold">The Bible is empty.</div>
          <p className="max-w-sm text-[13.5px] leading-relaxed text-muted-foreground">
            {hasSynopsis
              ? <>Hit <b>Fill all Bibles</b> to populate Story Bible, World Builder, Plot Extraction, Characters with arcs, and a Relationship Map — all in one pass. Or <b>Read manuscript</b> if chapters are already drafted.</>
              : <>Hit <b>Read manuscript</b> and this novel's model reads every drafted chapter, then fills the Story Bible and adds a card for every character it finds. You can also import a synopsis from{' '}<b>File → Import</b>, or add characters by hand.</>
            }
          </p>
          {hasSynopsis && (
            <Button
              size="sm"
              className="mt-1 gap-1.5 bg-primary hover:bg-primary/90"
              onClick={handleFillFromSynopsis}
              disabled={inferringBibles}
              data-testid="bible-empty-fill-from-synopsis"
            >
              {inferringBibles ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {inferringBibles ? 'Populating all bibles…' : 'Fill all Bibles from Synopsis'}
            </Button>
          )}
          <Button
            size="sm"
            className="mt-1 gap-1.5"
            variant={hasSynopsis ? 'outline' : 'default'}
            onClick={handleReadManuscript}
            disabled={readingMs}
            data-testid="bible-empty-read-manuscript"
          >
            {readingMs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {readingMs ? 'Reading the manuscript…' : 'Read manuscript & fill the Bible'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={handleAdd}
            data-testid="bible-empty-add"
          >
            <Plus className="h-3.5 w-3.5" /> Add first character
          </Button>
        </div>
      </PanelFrame>
    );
  }

  return (
    <>
    <PanelFrame
      title={t('characterBible.title')}
      icon={Users}
      panelId="characters"
      badge={`${characters.length}`}
      onClose={onClose}
      onMaximize={onMaximize}
      maximized={maximized}
      testId="bible-panel"
      actions={
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-1.5 text-[13px]"
            onClick={handleReadManuscript}
            disabled={readingMs}
            data-testid="bible-read-manuscript-btn"
            title="Read the manuscript and fill the Story Bible"
          >
            {readingMs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {readingMs ? 'Reading…' : <span className="font-bold">Read</span>}
          </Button>
          {ch && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-[13px] text-destructive hover:bg-destructive/10"
              onClick={() => setConfirmingDelete(ch)}
              data-testid="character-remove-btn"
              title={`${t('common.remove')} ${ch.name}`}
              disabled={characters.length <= 1}
            >
              <Trash2 className="h-3.5 w-3.5" /> {t('common.remove')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-1.5 text-[13px]"
            onClick={handleAdd}
            data-testid="character-add-btn"
            title={t('characterBible.addCharacter')}
          >
            <Plus className="h-3.5 w-3.5" /> <span className="font-bold">{t('common.add')}</span>
          </Button>
          {/* Bulk Series Sync — visible only when at least one character has sync enabled */}
          {syncEnabledCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-[13px] text-violet-700 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-500/10"
              onClick={openBulkSync}
              data-testid="bulk-series-sync-btn"
              title={`Sync ${syncEnabledCount} character${syncEnabledCount !== 1 ? 's' : ''} to Book 2+`}
            >
              <ArrowRightLeft className="h-3.5 w-3.5" />
              Sync All ({syncEnabledCount})
            </Button>
          )}
          {/* Full-screen modal toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setFsDialog(true)}
            data-testid="bible-fullscreen-btn"
            title="Open Character Bible in full screen"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      }
    >
      <div className="flex flex-1 min-h-0 flex-col">
        {/* iter55z-j — Character Bible selector (searchable dropdown, replaces the
            horizontal roster tabs so long character rosters no longer scroll off
            the right edge on mobile / narrow panels). */}
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-panel-border bg-panel-header/40 px-3 py-2">
          <CoachHint
            {...characterRowHint({
              hasAttributes:    !!(ch?.description || (ch?.traits || []).length > 0),
              hasBackstory:     !!(ch?.description && ch.description.length > 40),
              hasRelationships: !!(ch?.arc_summary && ch.arc_summary.length > 20),
              isActive:         !!ch,
              name:             ch?.name,
            })}
            side="bottom"
            align="start"
            testId="coach-character-selector"
          >
            <div>
          <Popover open={charPickerOpen} onOpenChange={setCharPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="character-bible-selector"
                className="h-8 justify-between gap-2 rounded-md px-2 text-[13.5px] font-medium"
                style={{ minWidth: '160px', maxWidth: '220px' }}
              >
                {ch ? (
                  <span className="flex items-center gap-2 truncate">
                    <span
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full text-[13px] font-semibold',
                        `bg-${ch.color}/20 text-${ch.color}`,
                      )}
                    >
                      {(ch.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                    </span>
                    <span className="truncate">{ch.name || 'Unnamed'}</span>
                    <span className="text-[13px] text-muted-foreground">· {ch.role || 'role?'}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select a character…</span>
                )}
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0 z-[300]" align="start">
              <Command className="min-h-0">
                <CommandInput placeholder="Search characters…" data-testid="character-bible-selector-input" />
                <CommandList className="max-h-[280px]">
                  <CommandEmpty>No characters match.</CommandEmpty>
                  <CommandGroup>
                    {characters.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={[c.name, c.role, c.archetype].filter(Boolean).join(' ').trim() || c.id}
                        onSelect={() => { setActiveCharacterId(c.id); setCharPickerOpen(false); }}
                        data-testid={`character-bible-option-${c.id}`}
                        className={cn(
                          'flex items-center gap-2',
                          c.id === ch?.id && 'bg-orange-500/10',
                        )}
                      >
                        <span className={cn(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[13px] font-bold',
                          c.id === ch?.id
                            ? 'bg-orange-500 text-white'
                            : `bg-${c.color}/20 text-${c.color}`,
                        )}>
                          {(c.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                        </span>
                        <span className="flex flex-col text-left leading-tight">
                          <span className={cn('text-[13.5px]', c.id === ch?.id ? 'font-bold text-orange-700 dark:text-orange-400' : 'font-medium')}>
                            {c.name}
                            {c.imported_from_synopsis && (
                              <span className="ml-1 rounded-sm border border-primary/30 bg-primary/10 px-1 text-[8.5px] font-semibold uppercase tracking-wider text-primary">
                                Imp
                              </span>
                            )}
                            {c.series_carryover && (
                              <span className="ml-1 rounded-sm border border-blue-500/40 bg-blue-500/10 px-1 text-[8.5px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400" title="Carries over to next book in the series">
                                Book 2+
                              </span>
                            )}
                          </span>
                          <span className="text-[13px] text-muted-foreground">
                            {c.role || 'no role'}
                            {c.archetype ? ` · ${c.archetype}` : ''}
                          </span>
                        </span>
                        {c.id === ch?.id && (
                          <span className="ml-auto text-[13px] font-bold text-orange-600 dark:text-orange-400">active</span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
            </div>
          </CoachHint>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleAdd}
            data-testid="character-bible-add-btn"
            className="h-8 gap-1 px-2 text-[13px]"
            title="Add a character"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateRelationshipMap}
            disabled={relMapLoading || characters.length < 2}
            data-testid="character-bible-map-relationships-btn"
            className="h-8 gap-1.5 px-2 text-[13px]"
            title={characters.length < 2 ? 'Add at least two characters first' : 'Read the Bible + manuscript and propose a relationship map'}
          >
            {relMapLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Network className="h-3.5 w-3.5" />}
            Map Relationships
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDedupeOpen(true)}
            disabled={characters.length < 2}
            data-testid="character-bible-find-dupes-btn"
            className="h-8 gap-1.5 px-2 text-[13px]"
            title="Scan the roster for duplicate or alias character entries and merge them"
          >
            <GitMerge className="h-3.5 w-3.5" />
            Find Duplicates
          </Button>
          <span className="ml-auto text-[13px] text-muted-foreground" data-testid="character-bible-count">
            {characters.length} in roster
          </span>
        </div>

        {/* Quick character search — always visible when ≥ 7 characters, or when
         *  the archetype apply event pre-fills it. Lets writers jump to any
         *  character without opening the Popover each time. */}
        {characters.length >= 7 || charSearch ? (
          <div
            className="relative shrink-0 border-b border-panel-border bg-panel-header/30 px-3 py-1.5"
            data-testid="char-quick-search-bar"
          >
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={charSearchRef}
                type="search"
                value={charSearch}
                onChange={(e) => setCharSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setCharSearch('');
                  if (e.key === 'Enter') {
                    const first = characters.find((c) =>
                      (c.name || '').toLowerCase().includes(charSearch.toLowerCase()),
                    );
                    if (first) { setActiveCharacterId(first.id); setCharSearch(''); }
                  }
                }}
                placeholder="Jump to character…"
                className="h-7 w-full rounded-sm border border-panel-border bg-background pl-7 pr-2 text-[13.5px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/40"
                data-testid="char-quick-search-input"
              />
            </div>
            {/* Filtered results — portalled to body so overflow-hidden panels don't clip it */}
            {charSearch.trim() && (() => {
              const ref = charSearchRef.current;
              if (!ref) return null;
              const rect = ref.getBoundingClientRect();
              const filtered = characters.filter((c) => (c.name || '').toLowerCase().includes(charSearch.toLowerCase()));
              return ReactDOM.createPortal(
                <div
                  className="fixed z-[350] mt-0.5 max-h-44 overflow-y-auto rounded-md border border-border bg-card shadow-md"
                  style={{ top: rect.bottom + 4, left: rect.left, width: rect.width }}
                >
                  {filtered.slice(0, 8).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault(); // prevent input blur clearing charSearch before click fires
                        setActiveCharacterId(c.id);
                        setCharSearch('');
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13.5px] transition-colors hover:bg-muted/40',
                        c.id === ch?.id && 'bg-primary/8 font-semibold',
                      )}
                      data-testid={`char-quick-search-option-${c.id}`}
                    >
                      <span className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold',
                        `bg-${c.color}/20 text-${c.color}`,
                      )}>
                        {(c.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('')}
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="shrink-0 text-[13px] text-muted-foreground">{c.role || ''}</span>
                      {c.archetype && (
                        <span className="shrink-0 rounded-full border px-1.5 text-[9px] text-muted-foreground">{c.archetype}</span>
                      )}
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <div className="px-3 py-2 text-[13px] text-muted-foreground">No characters match "{charSearch}".</div>
                  )}
                </div>,
                document.body,
              );
            })()}
          </div>
        ) : null}

        <RelationshipMapPanel
          rows={relationshipRows}
          characters={characters}
          saving={relMapSaving}
          onSave={handleSaveRelationshipMap}
        />

        {/* Body — Story Bible, Relationship Map, and character fields all scroll together
            so the panel never clips content below the frame regardless of panel height. */}
        <div className="flex-1 overflow-auto scroll-thin">
          {/* iter-48 — Story Bible summary (moved inside scroll so expanding it
              doesn't push character fields off screen). */}
          {(hasBible || project?.series_bible_id) && (
            <StoryBibleSummary
              bible={bible}
              open={bibleOpen}
              onToggle={() => setBibleOpen((v) => !v)}
              onOpenDialog={() => setBibleDialogOpen(true)}
              seriesBibleId={project?.series_bible_id}
            />
          )}
          {/* Relationship Map inside scroll for same reason */}
          <RelationshipMapPanel
            rows={relationshipRows}
            characters={characters}
            saving={relMapSaving}
            onSave={handleSaveRelationshipMap}
          />
          <div className="grid grid-cols-1 gap-3 p-3 pb-8 lg:grid-cols-[1fr_220px]">
            <div className="flex flex-col gap-3">
              {/* iter55f58 — "Identity" block (Name / Role / Age /
               * Archetype fields + archetype dropdown) removed per user
               * request. These technical fields now live in the
               * Advanced Modeling → Character Identity panel so the
               * Character Bible stays focused on prose (Description,
               * Traits, Voice, Story-role). */}
              <div className="rounded-md border border-dashed border-panel-border/70 bg-card/60 p-2 text-[13px] text-muted-foreground">
                Identity & archetype fields moved to
                <span className="mx-1 font-semibold text-foreground">Advanced Modeling → Character Identity</span>
                (top-nav → Advanced Modeling menu).
              </div>

              <div className="rounded-md border border-panel-border bg-card p-3">
                <div className="mb-2 flex items-center gap-2">
                  <BookHeart className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[13px] font-bold uppercase tracking-wider text-foreground">Description</span>
                </div>
                <Textarea
                  key={`desc-${ch.id}`}
                  defaultValue={ch.description}
                  onBlur={(e) => persistField('description', e.target.value)}
                  rows={3}
                  spellCheck
                  data-testid="character-description"
                  data-bible-field="character.description"
                  className="resize-none border-border bg-background text-[14px] leading-relaxed"
                />
                {ch.arc_summary && (
                  <div className="mt-2 rounded-sm border border-primary/20 bg-primary/5 p-2" data-testid="character-arc-summary">
                    <div className="mb-0.5 text-[9.5px] uppercase tracking-wider text-primary">Imported arc</div>
                    <p className="text-[13.5px] leading-snug text-foreground/85">{ch.arc_summary}</p>
                  </div>
                )}
              </div>

              <div className="rounded-md border border-panel-border bg-card p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[13px] font-bold uppercase tracking-wider text-foreground">Voice Sheet</span>
                  <button className="text-[13px] text-primary hover:underline">Hear sample…</button>
                </div>
                <Textarea
                  key={`voice-${ch.id}`}
                  defaultValue={ch.voice}
                  onBlur={(e) => persistField('voice', e.target.value)}
                  rows={2}
                  spellCheck
                  data-testid="character-voice"
                  data-bible-field="character.voice"
                  className="resize-none border-border bg-background text-[14px]"
                />
              </div>

              {/* iter55g4 — 5-Tier Character Bible (Basic Identity → Story Integration).
                   FiveTierBible does its own PUT; we only mirror the response into
                   the local store to avoid a double round-trip. */}
              <FiveTierBible
                character={ch}
                onCharacterUpdate={(next) => {
                  setCharacters((prev) => prev.map((c) => (c.id === next.id ? { ...c, ...next } : c)));
                }}
              />

              <div className="rounded-md border border-panel-border bg-card p-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[13px] font-bold uppercase tracking-wider text-foreground">Traits · tailor &amp; adjust</span>
                  <span className="font-mono-stat text-[13px] text-muted-foreground">0–100</span>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {ch.traits.map(t => (
                    <div key={t.k}>
                      <div className="mb-1 flex items-baseline justify-between">
                        <span className="text-[13.5px] font-medium">{t.k}</span>
                        <span className={cn('font-mono-stat text-[13px]', `text-${ch.color}`)}>{t.v}</span>
                      </div>
                      <Slider value={[t.v]} onValueChange={([v]) => updateTrait(t.k, v)} min={0} max={100} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="rounded-md border border-panel-border bg-card p-2">
                <div className="text-[13px] uppercase tracking-wider text-muted-foreground px-1">Trait Radar</div>
                <div className="h-[180px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={ch.traits.map(t => ({ subject: t.k, value: t.v }))}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                      <Radar name={ch.name} dataKey="value" stroke={`hsl(var(--${ch.color}))`} fill={`hsl(var(--${ch.color}))`} fillOpacity={0.35} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-md border border-panel-border bg-card p-3">
                <div className="text-[13px] uppercase tracking-wider text-muted-foreground">Continuity</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-mono-stat text-2xl font-semibold text-success">96<span className="text-sm text-muted-foreground">%</span></span>
                </div>
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">No trait drift detected across Chapters 1–6. One soft mismatch in Ch 4 (eye color reference).</p>
              </div>

              <Button variant="outline" size="sm" className="justify-between h-8 text-[13.5px]">
                Full Bible PDF <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PanelFrame>
    {/* iter55h — Archetype confirmation modal. Shows the pending
        archetype's blurb + example, plus a preview of what Claude
        will do. Commit calls the API and closes on success. */}
    <Dialog open={!!pending} onOpenChange={(open) => { if (!open) setPending(null); }}>
      <DialogContent className="max-w-lg" data-testid="archetype-commit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {pending?.name}
          </DialogTitle>
          <DialogDescription className="text-[13px] uppercase tracking-wider text-muted-foreground">
            {pending?.group}
          </DialogDescription>
        </DialogHeader>
        {pending && (
          <div className="space-y-3 text-[14px] leading-relaxed">
            <p className="italic text-foreground/90">{pending.blurb}</p>
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground/80">Example: </span>
              {pending.example}
            </p>
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-[13.5px] leading-snug">
              On commit, Claude will re-tune {ch?.name}&apos;s trait sliders
              ({(ch?.traits || []).map((t) => t.k).join(', ')}) to fit this
              archetype and rewrite the character description in two or three
              sentences. Nothing else changes.
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setPending(null)} data-testid="archetype-cancel">
            Cancel
          </Button>
          <Button
            onClick={applyArchetype}
            disabled={applying}
            data-testid="archetype-commit"
            className="inline-flex items-center gap-1.5"
          >
            {applying
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Re-tuning…</>
              : <><Sparkles className="h-3.5 w-3.5" /> Commit archetype</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* iter55t — Remove-character confirmation. Prevents accidental
        one-click deletions and disallows removing the last character. */}
    <Dialog open={!!confirmingDelete} onOpenChange={(open) => { if (!open) setConfirmingDelete(null); }}>
      <DialogContent className="max-w-sm" data-testid="character-remove-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" /> Remove {confirmingDelete?.name}?
          </DialogTitle>
          <DialogDescription>
            This deletes the character from the Bible. Any narrative
            references in your manuscript are left intact — you can
            re-add the character later, but the trait sliders, voice
            sheet and imported arc will be gone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setConfirmingDelete(null)} data-testid="character-remove-cancel">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={async () => {
              const doomed = confirmingDelete;
              setConfirmingDelete(null);
              if (doomed) await removeCharacter(doomed.id);
            }}
            data-testid="character-remove-confirm"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* iter55z-j — Story Bible in a scrollable modal */}
    <StoryBibleDialog
      open={bibleDialogOpen}
      onOpenChange={setBibleDialogOpen}
      bible={bible}
      projectTitle={project?.title}
      projectId={project?.id}
      onBibleUpdate={(newBible) => {
        if (newBible) setProject((p) => ({ ...p, story_bible: newBible }));
      }}
      onFieldSave={async (key, value) => {
        await updateProject({ story_bible: { [key]: value } });
      }}
    />

    {/* Full-screen Character Bible modal */}
    <Dialog open={fsDialog} onOpenChange={setFsDialog}>
      <DialogContent className="max-h-[92vh] w-[96vw] max-w-[1200px] overflow-auto p-0" data-testid="bible-fullscreen-dialog">
        <DialogHeader className="flex flex-row items-center justify-between border-b border-panel-border px-4 py-3">
          <DialogTitle className="flex items-center gap-2 text-[15px] font-bold">
            <Users className="h-4 w-4 text-primary" />
            Character Bible — {project?.title || 'Manuscript'}
          </DialogTitle>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setFsDialog(false)} data-testid="bible-fullscreen-close">
            <Minimize2 className="h-4 w-4" />
          </Button>
        </DialogHeader>
        <div className="p-4">
          {ch && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr]">
              {/* Left column: description + voice + traits */}
              <div className="flex flex-col gap-4">
                <div>
                  <div className="mb-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">Description</div>
                  <Textarea
                    key={`fs-desc-${ch.id}`}
                    defaultValue={ch.description}
                    onBlur={(e) => persistField('description', e.target.value)}
                    rows={5}
                    spellCheck
                    className="resize-none text-[13px] leading-relaxed"
                  />
                </div>
                <div>
                  <div className="mb-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">Voice Sheet</div>
                  <Textarea
                    key={`fs-voice-${ch.id}`}
                    defaultValue={ch.voice}
                    onBlur={(e) => persistField('voice', e.target.value)}
                    rows={3}
                    spellCheck
                    className="resize-none text-[13px]"
                  />
                </div>
                <div>
                  <div className="mb-2 text-[13px] font-bold uppercase tracking-wider text-foreground">Traits</div>
                  <div className="grid grid-cols-2 gap-3">
                    {ch.traits.map(t => (
                      <div key={t.k}>
                        <div className="mb-1 flex items-baseline justify-between">
                          <span className="text-[13.5px] font-semibold">{t.k}</span>
                          <span className={cn('font-mono-stat text-[13px]', `text-${ch.color}`)}>{t.v}</span>
                        </div>
                        <Slider value={[t.v]} onValueChange={([v]) => updateTrait(t.k, v)} min={0} max={100} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Right column: 5-tier bible */}
              <div>
                <div className="mb-2 text-[13px] font-bold uppercase tracking-wider text-foreground">5-Tier Character Bible</div>
                <FiveTierBible
                  character={ch}
                  onCharacterUpdate={(next) => {
                    setCharacters((prev) => prev.map((c) => (c.id === next.id ? { ...c, ...next } : c)));
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Find & Merge Duplicates dialog */}
    <CharacterDedupeDialog
      open={dedupeOpen}
      onClose={() => setDedupeOpen(false)}
      projectId={project?.id}
      onMerged={(resolved) => {
        // Remove deleted character IDs from the store and reload from API
        const deletedIds = resolved.map((r) => r.deleteId);
        const updatedKept = resolved.map((r) => r.kept).filter(Boolean);
        setCharacters((prev) =>
          prev
            .filter((c) => !deletedIds.includes(c.id))
            .map((c) => {
              const kept = updatedKept.find((k) => k.id === c.id);
              return kept ? { ...c, ...kept } : c;
            }),
        );
      }}
    />

    {/* Duplicate character detection dialog */}
    <Dialog open={dupDialogOpen} onOpenChange={setDupDialogOpen}>
      <DialogContent className="max-w-sm" data-testid="dup-character-dialog">
        <DialogHeader>
          <DialogTitle className="text-[14px] font-bold">Possible duplicate character</DialogTitle>
          <DialogDescription className="text-[13.5px]">
            "{dupCheckResult?.name}" may be the same person as an existing character. 
            Is this a new character, or do you want to edit the existing one?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          {(dupCheckResult?.matches || []).map((m) => (
            <button
              key={m.id}
              type="button"
              className="w-full rounded-md border border-panel-border bg-card p-2 text-left hover:border-orange-500/50 hover:bg-orange-500/5 transition"
              onClick={() => {
                setActiveCharacterId(m.id);
                setDupDialogOpen(false);
              }}
              data-testid={`dup-open-existing-${m.id}`}
            >
              <div className="text-[13.5px] font-semibold text-foreground">{m.name}</div>
              {m.reason && <div className="text-[13px] text-muted-foreground">{m.reason}</div>}
              <div className="mt-0.5 text-[13px] font-medium text-orange-600 dark:text-orange-400">
                → Open this character's Bible
              </div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDupDialogOpen(false)} data-testid="dup-keep-new-btn">
            Keep as new character
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Bulk Series Sync dialog */}
    <Dialog open={bulkSyncOpen} onOpenChange={setBulkSyncOpen}>
      <DialogContent className="max-w-sm" data-testid="bulk-series-sync-dialog">
        <DialogHeader>
          <DialogTitle>Sync All to Book 2+</DialogTitle>
          <DialogDescription>
            {syncEnabledCount} character{syncEnabledCount !== 1 ? 's' : ''} with Series Carry-over enabled will be pushed to the selected project.
            Physical traits, story role, arc status, and custom notes will be copied.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="block">
            <span className="text-[12px] font-medium text-foreground mb-1 block">Target project (Book 2+)</span>
            <select
              value={bulkSyncTargetId}
              onChange={(e) => setBulkSyncTargetId(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5 text-[13px] outline-none focus:border-primary"
              data-testid="bulk-sync-project-select"
            >
              <option value="">Select a project…</option>
              {bulkSyncProjects.map(p => (
                <option key={p.id} value={p.id}>{p.title || '(Untitled)'}</option>
              ))}
            </select>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setBulkSyncOpen(false)}>Cancel</Button>
          <Button
            size="sm"
            onClick={runBulkSync}
            disabled={bulkSyncing || !bulkSyncTargetId}
            className="gap-1"
            data-testid="bulk-sync-confirm-btn"
          >
            {bulkSyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}
            {bulkSyncing ? 'Syncing…' : `Sync ${syncEnabledCount} Character${syncEnabledCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function Field({ label, value, onBlur, testId, type }) {
  return (
    <div>
      <div className="mb-1 text-[13px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <Input
        // key by value so switching characters resets the uncontrolled input
        // to the new character's field (instead of stubbornly keeping the
        // previous character's typed value).
        key={value}
        defaultValue={value}
        type={type || 'text'}
        onBlur={(e) => onBlur && onBlur(e.target.value)}
        data-testid={testId}
        className="h-7 border-border bg-background text-[14px]"
      />
    </div>
  );
}
